import { AuthorizationStatusView } from 'connections-embed-lib';
import { FavoriteIcon } from 'foundations-assets/icons/components/FavoriteIcon';
import { HubspotCertificationIcon } from 'foundations-assets/icons/components/HubspotCertificationIcon';
import { LocationIcon } from 'foundations-assets/icons/components/LocationIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import unescapedText from 'I18n/utils/unescapedText';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { getReviewTypeFromProductType } from 'marketplace-ui-reviews/utils/getReviewTypeFromProductType';
import { useCallback, useMemo } from 'react';
import { BreezeBadge, DisplayTagBadge, ProductTypeBadge } from 'trellis-ecosystem';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { DefaultButton } from '../../install/DefaultButton';
import { useAppStatus } from '../../install/hooks/useAppStatus';
import { useAppUninstallableReason } from '../../install/hooks/useAppUninstallableReason';
import { SolutionsPartnerTierIcon } from '../../tierIcons/SolutionsPartnerTierIcon';
import { buildFullAddress, buildFullCity, getGoogleMapsUrl } from '../../utils/locationUtils';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../../utils/TrackingUtils';
import { DetailsPageCard } from './DetailsPageCard';
import { getTemplateIcon } from '../../utils/templateUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TIER_MESSAGE_KEYS = {
  elite: 'detailsPage.pricingCard.tier.elite',
  diamond: 'detailsPage.pricingCard.tier.diamond',
  platinum: 'detailsPage.pricingCard.tier.platinum',
  gold: 'detailsPage.pricingCard.tier.gold',
  none: 'detailsPage.pricingCard.tier.none'
};
export function ProductBuyBox({
  agentType,
  betaDetails,
  certifiedAt,
  companyName,
  companyUrl,
  contactForm,
  installCount,
  listingTrellisIcon,
  meetingsUrl,
  tagline,
  partnerType,
  physicalOfficeLocations,
  primaryCTA,
  productId,
  remoteLocations,
  sourceId,
  tier
}) {
  var _reviewStats$axes$get, _reviewStats$axes$get2;
  const {
    appContext,
    iconUrl,
    displayTag,
    listingId,
    productType,
    published,
    runLeadGen,
    name,
    trackInteraction,
    worksWithBreeze
  } = useDetailsPageContext();
  const isSolutionsPartnerProfile = productType === 'SOLUTIONS_PARTNER_PROFILE';
  const displayName = isSolutionsPartnerProfile ? companyName : name;
  const {
    statusData,
    statusLoading
  } = useAppStatus({
    appId: sourceId,
    shouldFetch: productType === 'APP' && appContext === 'app'
  });
  const {
    uninstallableReason
  } = useAppUninstallableReason({
    appContext,
    listingId,
    features: [],
    // unused for now, but required by the hook
    published,
    skip: isSolutionsPartnerProfile
  });
  const isAppInstalled = (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED';
  const {
    reviewStats
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      entityId: productId,
      limit: 2
    }),
    offeringId: productId,
    reviewType: getReviewTypeFromProductType(productType)
  });
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product buy box'
    }, eventProps));
  }, [trackInteraction]);
  const scrollToReviews = () => {
    const tab = document.querySelector(`#details-page-reviews`);
    if (tab) {
      tab.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const renderedLocations = useMemo(() => {
    if (!physicalOfficeLocations && !remoteLocations) {
      return null;
    }
    if (physicalOfficeLocations) {
      return physicalOfficeLocations.map((location, index) => {
        const {
          physicalOfficeLocation
        } = location;
        return /*#__PURE__*/_jsxs(View, {
          children: [/*#__PURE__*/_jsx(View, {
            as: "span",
            marginInlineEnd: "100",
            children: /*#__PURE__*/_jsx(LocationIcon, {
              color: "icon-core-subtle",
              size: "300"
            })
          }), /*#__PURE__*/_jsx(Link, {
            external: true,
            href: getGoogleMapsUrl(buildFullAddress({
              location: physicalOfficeLocation
            })),
            onClick: () => {
              handleTrackInteraction({
                action: 'click physical office location link'
              });
              trackCustomMarketplaceOfferingBehavioralEvent('LOCATION');
            },
            children: buildFullCity({
              location: physicalOfficeLocation
            })
          })]
        }, index);
      });
    }
    if (remoteLocations) {
      return /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(LocationIcon, {
            color: "icon-core-subtle",
            size: "300"
          })
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.locations.remote"
        }), /*#__PURE__*/_jsx(HelpIcon, {
          "aria-label": unescapedText('detailsPage.productDetailsCard.locations.remoteTooltipAccessibleName'),
          marginInlineStart: "100",
          role: "tooltip",
          tooltipPlacement: "right",
          tooltipProps: {
            maxInlineSize: '250px'
          },
          title: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.locations.remoteTooltip",
              options: {
                companyName
              }
            }), /*#__PURE__*/_jsx(List, {
              styled: true,
              children: remoteLocations.map((location, index) => {
                const {
                  remoteLocation
                } = location;
                return /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(Link, {
                    external: true,
                    href: getGoogleMapsUrl(buildFullAddress({
                      location: remoteLocation,
                      remote: true
                    })),
                    onClick: () => {
                      handleTrackInteraction({
                        action: 'click remote location link'
                      });
                      trackCustomMarketplaceOfferingBehavioralEvent('LOCATION');
                    },
                    children: buildFullCity({
                      location: remoteLocation,
                      remote: true
                    })
                  })
                }, index);
              })
            })]
          })
        })]
      });
    }
  }, [companyName, handleTrackInteraction, physicalOfficeLocations, remoteLocations]);
  const renderedPricingMessage = useMemo(() => {
    if (productType === 'APP') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.pricingMessage.subscription",
        options: {
          name
        }
      });
    }
    // TODO: reinstate when pricing is finalized
    // if (productType === 'AGENT') {
    //   return (
    //     <FormattedMessage message="detailsPage.productDetailsCard.pricingMessage.credits" />
    //   );
    // }
    return null;
  }, [productType, name]);
  const renderedScheduleDemoButton = useMemo(() => {
    return meetingsUrl && contactForm ? /*#__PURE__*/_jsx(Dropdown, {
      buttonText: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.scheduleDemoButton"
        })
      }),
      buttonStyles: {
        inlineSize: '100%'
      },
      onClick: () => handleTrackInteraction({
        action: 'click schedule demo dropdown'
      }),
      buttonUse: "secondary",
      marginInline: "0",
      children: /*#__PURE__*/_jsxs(List, {
        children: [/*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: meetingsUrl,
            external: true,
            onClick: () => {
              runLeadGen({
                action: 'CLICKED_SCHEDULE_MEETING'
              });
              handleTrackInteraction({
                action: 'click schedule meeting'
              });
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.scheduleMeetingButton"
            })
          })
        }), /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: contactForm,
            external: true,
            onClick: () => {
              runLeadGen({
                action: 'CLICKED_REQUEST_CONTACT'
              });
              handleTrackInteraction({
                action: 'click request to be contacted'
              });
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.requestToBeContactedButton"
            })
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(Button, {
      href: meetingsUrl || contactForm,
      block: true,
      external: true,
      onClick: () => {
        if (meetingsUrl) {
          runLeadGen({
            action: 'CLICKED_SCHEDULE_MEETING'
          });
          handleTrackInteraction({
            action: 'click schedule demo'
          });
        } else {
          runLeadGen({
            action: 'CLICKED_REQUEST_CONTACT'
          });
          handleTrackInteraction({
            action: 'click request to be contacted'
          });
        }
      },
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: meetingsUrl ? 'detailsPage.productDetailsCard.cta.scheduleDemoButton' : 'detailsPage.productDetailsCard.cta.requestToBeContactedButton'
      })
    });
  }, [meetingsUrl, contactForm, handleTrackInteraction, runLeadGen]);
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    name: "buy box",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: [iconUrl && /*#__PURE__*/_jsx(Image, {
        blockSize: isSolutionsPartnerProfile ? undefined : '72px',
        maxBlockSize: isSolutionsPartnerProfile ? '72px' : undefined,
        inlineSize: isSolutionsPartnerProfile ? undefined : '72px',
        maxInlineSize: isSolutionsPartnerProfile ? '100%' : undefined,
        objectFit: isSolutionsPartnerProfile ? undefined : 'cover',
        src: iconUrl,
        alt: `${name}-icon`
      }), listingTrellisIcon && /*#__PURE__*/_jsx(IconHolder, {
        paddingBlockStart: "0",
        paddingInlineStart: "0",
        marginBlockStart: "0",
        marginInlineStart: "0",
        size: "500",
        icon: getTemplateIcon(listingTrellisIcon)
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        flexDirection: "row",
        gap: "100",
        maxBlockSize: "200",
        marginBlockStart: "100",
        children: [!isSolutionsPartnerProfile && /*#__PURE__*/_jsx(Flex, {
          "data-test-id": "display-tag-badge",
          children: displayTag ? /*#__PURE__*/_jsx(DisplayTagBadge, {
            displayTagLabel: displayTag
          }) : /*#__PURE__*/_jsx(ProductTypeBadge, {
            productType: productType,
            agentType: agentType
          })
        }), worksWithBreeze && /*#__PURE__*/_jsx(Flex, {
          "data-test-id": "breeze-badge",
          children: /*#__PURE__*/_jsx(BreezeBadge, {})
        }), betaDetails && /*#__PURE__*/_jsx(Flex, {
          "data-test-id": "beta-badge",
          children: /*#__PURE__*/_jsx(Badge, {
            use: "beta",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.pricingCard.beta"
            })
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      paddingBlockStart: "200",
      paddingBlockEnd: "300",
      paddingInline: "0",
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      gap: "200",
      children: [displayName && /*#__PURE__*/_jsx(Text, {
        as: "h1",
        typography: "heading-300",
        textWrap: "wrap",
        truncate: {
          lineClamp: 2
        },
        children: displayName
      }), tier && /*#__PURE__*/_jsxs(Text, {
        typography: "heading-25",
        textTransform: "uppercase",
        children: [/*#__PURE__*/_jsx(SolutionsPartnerTierIcon, {
          bordered: true,
          marginInlineEnd: "100",
          partnerType: partnerType,
          size: 24,
          tier: tier
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: TIER_MESSAGE_KEYS[tier]
        })]
      }), productType === 'APP' && certifiedAt && /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom",
        title: unescapedText('detailsPage.productDetailsCard.certified.tooltip'),
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexDirection: "row",
          marginBlockEnd: "300",
          gap: "100",
          children: [/*#__PURE__*/_jsx(HubspotCertificationIcon, {
            color: "fill-brand-default",
            size: "600"
          }), /*#__PURE__*/_jsx(Text, {
            textTransform: "uppercase",
            typography: "heading-25",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.certified.text"
            })
          })]
        })
      }), tagline && /*#__PURE__*/_jsx(Text, {
        as: "div",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(TruncateMultiline, {
          text: tagline,
          maxChars: 70
        })
      }), renderedLocations && /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(Text, {
          color: "text-core-subtle",
          typography: "detail-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.locations.text"
          })
        }), renderedLocations]
      }), /*#__PURE__*/_jsxs(DescriptionList, {
        columnGap: "300",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        inlineSize: "100%",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: isSolutionsPartnerProfile ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.website"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.builtBy"
            })
          }), /*#__PURE__*/_jsx("dd", {
            children: companyUrl ? /*#__PURE__*/_jsx(Link, {
              href: companyUrl,
              external: true,
              onClick: () => {
                handleTrackInteraction({
                  action: 'click company website link'
                });
                runLeadGen({
                  action: 'CLICKED_COMPANY_WEBSITE'
                });
                trackCustomMarketplaceOfferingBehavioralEvent('COMPANY_WEBSITE');
              },
              "data-test-id": "company-name",
              children: companyName
            }) : /*#__PURE__*/_jsx(Text, {
              "data-test-id": "company-name",
              children: companyName
            })
          })]
        }), typeof installCount === 'number' && /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.installs"
            })
          }), /*#__PURE__*/_jsx("dd", {
            "data-test-id": "install-count",
            children: formatInstalls(installCount)
          })]
        }), !!reviewStats.count && !!((_reviewStats$axes$get = reviewStats.axes.get('OVERALL')) !== null && _reviewStats$axes$get !== void 0 && _reviewStats$axes$get.average) && /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.rating"
            })
          }), /*#__PURE__*/_jsx("dd", {
            children: /*#__PURE__*/_jsx(Link, {
              onClick: () => {
                handleTrackInteraction({
                  action: 'click reviews rating link'
                });
                scrollToReviews();
              },
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                flexDirection: "row",
                children: [/*#__PURE__*/_jsx(Text, {
                  color: "text-core-default",
                  children: /*#__PURE__*/_jsx(FormattedNumber, {
                    abbreviate: true,
                    maxFractionDigits: 1,
                    value: ((_reviewStats$axes$get2 = reviewStats.axes.get('OVERALL')) === null || _reviewStats$axes$get2 === void 0 ? void 0 : _reviewStats$axes$get2.average) || 0
                  })
                }), /*#__PURE__*/_jsx(IconHolder, {
                  icon: FavoriteIcon,
                  paddingBlock: "0",
                  paddingInlineStart: "100",
                  size: "200"
                }), /*#__PURE__*/_jsx(View, {
                  as: "span",
                  marginInlineStart: "-200",
                  children: /*#__PURE__*/_jsxs(Text, {
                    color: "text-core-default",
                    children: ["(", /*#__PURE__*/_jsx(FormattedNumber, {
                      abbreviate: true,
                      value: reviewStats.count || 0
                    }), ")"]
                  })
                })]
              })
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexGrow: 1,
      padding: "0",
      display: "flex",
      flexDirection: "column",
      gap: "200",
      children: [primaryCTA !== null && primaryCTA !== void 0 ? primaryCTA : /*#__PURE__*/_jsx(DefaultButton, {}), !isAppInstalled && /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-core-subtle",
        textAlign: "center",
        children: renderedPricingMessage
      }), (meetingsUrl || contactForm) &&
      /*#__PURE__*/
      // This is wrapped in a TFlex to ensure the button is full width -
      // when two buttons are adjacent, the second button gets a forced inlineMarginStart of 12px
      _jsx(TFlex, {
        children: renderedScheduleDemoButton
      }), isAppInstalled && statusData && /*#__PURE__*/_jsx(TFlex, {
        align: "center",
        direction: "column",
        children: /*#__PURE__*/_jsx(AuthorizationStatusView, {
          accountStatusesList: statusData,
          loading: statusLoading
        })
      })]
    })]
  });
}